import { Row, Col, Table, Button, Tag, message } from "antd";
import { ColumnsType } from "antd/lib/table";
import { AppActionStrip } from "components/AppActionStrip/AppActionStrip";
import { AppFilter, AppFilterButton, AppEarningPayoutsFilter } from "components/AppFilter";
import { AppPageHeader } from "components/AppPageHeader";
import { useDocumentTitle } from "hooks/usePageTitle";
import { useCallback, useEffect, useMemo, useState } from "react";
import moment from "moment";
import { useNavigate, useSearchParams } from "react-router-dom";
import { DownloadOutlined, UploadOutlined } from "@ant-design/icons";
import { IGigWorkerEarning } from "store/partner-services/partnerServicesSlice";
import { PartnerVariant, usePartnerServicesConfig } from "hooks/usePartnerServicesConfig";

const MESSAGE_KEY = "earning_payouts";

const statusColorMap: Record<string, string> = {
  CREATED: "green",
  NOT_CREATED: "red",
  PENDING: "orange",
};

const EarningPayoutsPage = ({ variant = "ppd" }: { variant?: PartnerVariant }) => {
  useDocumentTitle("Earning Payouts");
  const config = usePartnerServicesConfig(variant);
  const {
    dispatch,
    earningPayouts,
    status,
    earningPayoutsDownloadStatus,
    error,
    earningPayoutsThunk,
    downloadEarningPayoutsThunk,
    partnerRoutes,
  } = config;

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [filterState, setFilterState] = useState(false);

  const month = searchParams.get("month") ?? "";

  useEffect(() => {
    if (!month) {
      setSearchParams({ month: moment().format("YYYY-MM") }, { replace: true });
    }
  }, [month, setSearchParams]);

  // earningPayoutsThunk and dispatch are stable module-level refs — safe as deps.
  const getData = useCallback(() => {
    if (!month) return;
    dispatch(earningPayoutsThunk({ month }));
  }, [month, dispatch, earningPayoutsThunk]);

  useEffect(() => {
    getData();
  }, [getData]);

  useEffect(() => {
    if (status === "loading") {
      message.loading({ content: "Loading Earning Payouts...", key: MESSAGE_KEY });
    } else if (status === "error") {
      message.error({ content: error ?? "Something went wrong!", key: MESSAGE_KEY });
    } else {
      message.destroy(MESSAGE_KEY);
    }
    return () => {
      message.destroy(MESSAGE_KEY);
    };
  }, [status, error]);

  const handleDownload = async () => {
    const result = await dispatch(downloadEarningPayoutsThunk({ month }));
    if (downloadEarningPayoutsThunk.rejected.match(result)) {
      const errMsg = (result.payload as { message?: string })?.message;
      message.error({ content: errMsg ?? "Download failed!", key: MESSAGE_KEY });
    }
  };

  const filterCount = useMemo(
    () => ["month"].filter((k) => searchParams.get(k)).length,
    [searchParams]
  );

  const columns: ColumnsType<IGigWorkerEarning> = [
    { key: "userId", title: "User ID", dataIndex: "userId", width: 80 },
    {
      key: "name",
      title: "Name",
      dataIndex: "name",
      render: (val: string | null) => <>{val ?? "-"}</>,
    },
    {
      key: "mobile",
      title: "Mobile",
      dataIndex: "mobile",
      render: (val: string | null) => <>{val ?? "-"}</>,
    },
    {
      key: "mysaAccountStatus",
      title: "MYSA Account Status",
      dataIndex: "mysaAccountStatus",
      render: (val: string) => (
        <Tag color={statusColorMap[val] ?? "default"}>{val}</Tag>
      ),
    },
    {
      key: "mysaPartnerId",
      title: "MYSA Partner ID",
      dataIndex: "mysaPartnerId",
      render: (val: string | null) => <>{val ?? "-"}</>,
    },
    {
      key: "earnings",
      title: "Earnings (₹)",
      dataIndex: "earnings",
      render: (val: number | null) => <>{val != null ? val.toFixed(2) : "-"}</>,
    },
    { key: "earningDays", title: "Earning Days", dataIndex: "earningDays" },
    {
      key: "alteredEarnings",
      title: "Altered Earnings (₹)",
      dataIndex: "alteredEarnings",
      render: (val: number | null) => <>{val != null ? val.toFixed(2) : "-"}</>,
    },
    {
      key: "payableEarnings",
      title: "Payable Earnings (₹)",
      dataIndex: "payableEarnings",
      render: (val: number | null) => <>{val != null ? val.toFixed(2) : "-"}</>,
    },
    {
      key: "paidAmount",
      title: "Paid Amount (₹)",
      dataIndex: "paidAmount",
      render: (val: number | null) => <>{val != null ? val.toFixed(2) : "-"}</>,
    },
    {
      key: "pendingAmount",
      title: "Pending Amount (₹)",
      dataIndex: "pendingAmount",
      render: (val: number | null) => <>{val != null ? val.toFixed(2) : "-"}</>,
    },
  ];

  return (
    <>
      <Row>
        <Col>
          <AppPageHeader title="Earning Payouts" />
        </Col>
      </Row>
      <Row>
        <AppActionStrip>
          <div className="action-strip__item">
            <AppFilterButton
              active={filterState}
              onClick={() => setFilterState((s) => !s)}
              filterCount={filterCount}
            />
          </div>
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            onClick={handleDownload}
            loading={earningPayoutsDownloadStatus === "downloading"}
          >
            Download Earnings
          </Button>
          <Button
            icon={<UploadOutlined />}
            onClick={() => navigate(`/${partnerRoutes.UPLOAD_EARNING_PAYOUTS}`)}
          >
            Upload Earning Payouts
          </Button>
          <Button
            icon={<UploadOutlined />}
            onClick={() => navigate(`/${partnerRoutes.UPLOAD_ALTER_EARNINGS}`)}
          >
            Alter Earnings Upload
          </Button>
        </AppActionStrip>
      </Row>
      <Row style={{ marginBottom: "1rem" }}>
        <AppFilter expanded={filterState}>
          <AppEarningPayoutsFilter />
        </AppFilter>
      </Row>
      <Row>
        <Col xs={24}>
          <Table<IGigWorkerEarning>
            columns={columns}
            dataSource={earningPayouts}
            rowKey="userId"
            scroll={{ x: "100vh", y: "65vh" }}
            size="small"
            pagination={false}
            loading={status === "loading"}
            bordered
          />
        </Col>
      </Row>
    </>
  );
};

export default EarningPayoutsPage;
